import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import { useAuthState, useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { useEffect, useState } from 'react'
import { useLocation } from 'wouter'
import { Button } from '@app/components/core/button/Button'
import { DashboardListCard } from '@app/components/blocks/card/DashboardListCard'
import { pages } from '@app/util/links'
import { DashboardListRegionModel } from 'functions/app/dashboard/profile/region/list-followed'
import { DashboardListMunicipalityModel } from 'functions/app/dashboard/profile/municipality/list-followed'
import findLocationImage from '@app/assets/images/find-location-reversed.png'
import { FollowingEmptyState } from './FollowingEmptyState'

export const FollowedRegions = () => {
  const translations = useTranslations().app.dashboard.followed
  const client = useClient()
  const authState = useAuthState()
  const [followedRegions, setFollowedRegions] = useState<
    DashboardListRegionModel[]
  >([])
  const [followedMunicipalities, setFollowedMunicipalities] = useState<
    DashboardListMunicipalityModel[]
  >([])
  const [_, navigate] = useLocation()

  const renderRegionSection =
    followedRegions.length !== 0 || followedMunicipalities.length !== 0

  useEffect(() => {
    if (!authState.userId) {
      return
    }
    const close = client
      .query('list-followed-region', { userId: authState.userId })
      .subscribe((r) => {
        const mapped: DashboardListRegionModel[] = r.map((region: any) => ({
          ...region,
          image: region?.image?.src,
        }))
        setFollowedRegions(mapped)
      })
    return () => close()
  }, [client, authState.userId])

  useEffect(() => {
    if (!authState.userId) {
      return
    }
    const close = client
      .query('list-followed-municipality', { userId: authState.userId })
      .subscribe((r) => {
        const mapped: DashboardListMunicipalityModel[] = r.map(
          (region: any) => ({
            ...region,
            image: region?.image?.src,
          }),
        )
        setFollowedMunicipalities(mapped)
      })
    return () => close()
  }, [client, authState.userId])

  const unfollowRegion = async (regionId: number) => {
    await client.call('follow-region', {
      regionId: regionId,
    })
  }

  const unfollowMunicipality = async (municipalityId: number) => {
    await client.call('follow-municipality', {
      municipalityId: municipalityId,
    })
  }

  if (!renderRegionSection) {
    return (
      <FollowingEmptyState
        title={translations.regions}
        message={translations.youFollowNoRegions}
        imageSrc={findLocationImage}
        imageAlt={translations.youFollowNoRegions}
        discoverHref={pages.regions}
        discoverLabel={translations.discoverRegions}
      />
    )
  }

  return (
    <Column alignItems="stretch">
      <Title.H4 style={{ marginBottom: '0' }}>{translations.regions}</Title.H4>
      {followedRegions.map((region) => (
        <DashboardListCard
          key={region.code}
          title={region.name}
          subtitle={translations.province}
          hasBadgeTitle={region.crowdbuildingPartner}
          avatar={region.image}
          onClick={() => navigate(pages.regionPage(region.slug))}
          action={
            <Button
              variant="secondary"
              size="small"
              label={translations.unfollow}
              onClick={(e) => {
                e.stopPropagation()
                void unfollowRegion(region.id)
              }}
            />
          }
        />
      ))}
      {followedMunicipalities.map((municipality) => (
        <DashboardListCard
          key={municipality.code}
          title={municipality.name}
          subtitle={translations.municipality}
          hasBadgeTitle={municipality.crowdbuildingPartner}
          avatar={municipality.image?.src}
          onClick={() => navigate(pages.municipalityPage(municipality.slug))}
          action={
            <Button
              variant="secondary"
              size="small"
              label={translations.unfollow}
              onClick={(e) => {
                e.stopPropagation()
                void unfollowMunicipality(municipality.id)
              }}
            />
          }
        />
      ))}
    </Column>
  )
}
